<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError && touched,
      'form-group--has-success': !localError && touched,
    }"
  >
    <div class="sf-input sf-input--searchable-select">
      <slot name="label"></slot>
      <div
        class="searchable-select"
        :class="{ 'searchable-select--open': isOpen }"
        ref="containerRef"
      >
        <!-- Trigger: shows selected lead or placeholder -->
        <div
          class="searchable-select__trigger"
          :class="{ 'searchable-select__trigger--disabled': disabled }"
          @click="toggleDropdown"
          tabindex="0"
          @keydown.enter.prevent="toggleDropdown"
          @keydown.space.prevent="toggleDropdown"
          @keydown.escape="closeDropdown"
        >
          <div v-if="selectedLeadData" class="searchable-select__selected">
            <AvatarElement
              :title="
                selectedLeadData.name ? selectedLeadData.name.substring(0, 2).toUpperCase() : 'LD'
              "
              :color="'var(--brand-500)'"
              :textColor="'white'"
              size="xs"
            />
            <span class="searchable-select__selected-name">
              {{ selectedLeadData.name || $t('crm.no_name_entered') }}
            </span>
          </div>
          <span v-else class="searchable-select__placeholder-text">{{ placeholder }}</span>
          <BaseIcon name="chevron" size="16" class="searchable-select__chevron" />
        </div>

        <!-- Dropdown content (rendered into Tippy / appended to body) -->
        <div ref="dropdownRef" class="searchable-select__dropdown">
          <!-- Search input with magnify icon inside -->
          <div class="searchable-select__search">
            <BaseIcon name="magnify" size="14" class="searchable-select__search-icon" />
            <input
              ref="searchInputRef"
              type="text"
              v-model="searchTerm"
              :placeholder="$t('proposals.form.search_leads')"
              @input="onSearch"
              @keydown.escape="closeDropdown"
              @keydown.enter.prevent="selectHighlighted"
              @keydown.arrow-down.prevent="navigateDown"
              @keydown.arrow-up.prevent="navigateUp"
              class="searchable-select__search-input"
            />
          </div>

          <div v-if="isSearching" class="searchable-select__loading">
            <LoadingSpinner size="sm" />
            <span>{{ $t('proposals.form.search_leads') }}</span>
          </div>
          <div v-else-if="!searchResults.length" class="searchable-select__empty">
            {{
              searchTerm ? $t('proposals.form.no_leads_found') : $t('proposals.form.select_lead')
            }}
          </div>
          <div v-else class="searchable-select__options">
            <div
              v-for="(lead, index) in searchResults"
              :key="lead.id"
              class="searchable-select__option"
              :class="{
                'searchable-select__option--highlighted': index === highlightedIndex,
                'searchable-select__option--selected': lead.id === modelValue,
              }"
              @click="selectLead(lead)"
              @mouseenter="highlightedIndex = index"
            >
              <div class="lead-option">
                <AvatarElement
                  :title="lead.name ? lead.name.substring(0, 2).toUpperCase() : 'LD'"
                  :color="'var(--brand-500)'"
                  :textColor="'white'"
                  size="sm"
                />
                <div class="lead-option__info">
                  <div class="lead-option__name">{{ lead.name || $t('crm.no_name_entered') }}</div>
                  <div class="lead-option__email">{{ lead.email }}</div>
                </div>
                <span
                  class="lead-option__status"
                  :class="`lead-option__status--${lead.status || 'new'}`"
                >
                  <div class="status-dot" :class="`status-dot--${lead.status || 'new'}`"></div>
                  {{ getStatusLabel(lead.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Create lead footer -->
          <div v-if="showCreate" class="searchable-select__create" @click="onCreateClick">
            <span class="searchable-select__create-icon">+</span>
            {{ $t('crm.create_lead.title') }}
          </div>
        </div>
      </div>
      <span v-if="localError && touched" class="form-group__error">{{ localError }}</span>
    </div>
  </div>
</template>

<script>
  import {
    ref,
    watch,
    computed,
    onMounted,
    onBeforeUnmount,
    nextTick,
    getCurrentInstance,
  } from 'vue';
  import tippy from 'tippy.js';
  import { validateInput } from '@/utils/validation';
  import { useCrmStore } from '@/store/crmStore';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  export default {
    name: 'SearchableLeadSelect',
    components: {
      BaseIcon,
      AvatarElement,
      LoadingSpinner,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: String,
        default: '',
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      showCreate: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'validate', 'leadSelected', 'create'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const crmStore = useCrmStore();

      const localError = ref(props.error);
      const touched = ref(false);
      const isOpen = ref(false);
      const searchTerm = ref('');
      const searchResults = ref([]);
      const isSearching = ref(false);
      const highlightedIndex = ref(-1);
      const searchInputRef = ref(null);
      const containerRef = ref(null);
      const dropdownRef = ref(null);
      const searchDebounceTimer = ref(null);
      let tippyInstance = null;

      const getStatusLabel = (status) => {
        const statusLabels = {
          new: $t('crm.statuses.new'),
          contacted: $t('crm.statuses.contacted'),
          introduction: $t('crm.statuses.introduction'),
          qualified: $t('crm.statuses.qualified'),
          converted: $t('crm.statuses.converted'),
          lost: $t('crm.statuses.lost'),
        };
        return statusLabels[status] || $t('crm.statuses.new');
      };

      // Search results are not always in the store, so remember the pick.
      const pickedLead = ref(null);
      const selectedLeadData = computed(() => {
        if (!props.modelValue) return null;
        return (
          crmStore.leads.find((l) => l.id === props.modelValue) ||
          (pickedLead.value?.id === props.modelValue ? pickedLead.value : null)
        );
      });

      const openDropdown = async () => {
        if (props.disabled) return;
        isOpen.value = true;
        highlightedIndex.value = -1;
        searchTerm.value = '';
        if (crmStore.leads.length === 0) {
          await loadInitialLeads();
        } else {
          searchResults.value = crmStore.leads;
        }
        await nextTick();
        searchInputRef.value?.focus();
      };

      const closeDropdown = () => {
        isOpen.value = false;
        highlightedIndex.value = -1;
        touched.value = true;
        validate();
      };

      const toggleDropdown = () => {
        if (isOpen.value) {
          closeDropdown();
        } else {
          openDropdown();
        }
      };

      const onSearch = () => {
        highlightedIndex.value = -1;

        const term = searchTerm.value.trim();

        // Debounce: clear any pending timer
        if (searchDebounceTimer.value) {
          clearTimeout(searchDebounceTimer.value);
        }

        if (!term) {
          // Empty search — show all loaded leads immediately
          searchResults.value = crmStore.leads;
          return;
        }

        // Show results from already-loaded leads immediately for snappy feel
        const lowerTerm = term.toLowerCase();
        searchResults.value = crmStore.leads.filter((lead) => {
          const name = (lead.name || '').toLowerCase();
          const email = (lead.email || '').toLowerCase();
          const phone = (lead.phone || '').toLowerCase();
          const companyName = (lead.company_name || '').toLowerCase();
          return (
            name.includes(lowerTerm) ||
            email.includes(lowerTerm) ||
            phone.includes(lowerTerm) ||
            companyName.includes(lowerTerm)
          );
        });

        // Debounce API search to catch leads not yet in the local cache
        searchDebounceTimer.value = setTimeout(async () => {
          isSearching.value = true;
          try {
            const apiResults = await crmStore.searchLeads(term);
            searchResults.value = apiResults || [];
          } catch (error) {
            console.error('Error searching leads:', error);
          } finally {
            isSearching.value = false;
          }
        }, 300);
      };

      const loadInitialLeads = async () => {
        isSearching.value = true;
        try {
          if (crmStore.leads.length === 0) {
            await crmStore.fetchLeads({ page: 1, limit: 100 });
          }
          searchResults.value = crmStore.leads;
        } catch (error) {
          console.error('Error loading leads:', error);
          searchResults.value = [];
        } finally {
          isSearching.value = false;
        }
      };

      const selectLead = (lead) => {
        pickedLead.value = lead;
        emit('update:modelValue', lead.id);
        emit('leadSelected', lead);
        closeDropdown();
      };

      const onCreateClick = () => {
        closeDropdown();
        emit('create');
      };

      const selectHighlighted = () => {
        if (highlightedIndex.value >= 0 && searchResults.value[highlightedIndex.value]) {
          selectLead(searchResults.value[highlightedIndex.value]);
        }
      };

      const navigateDown = () => {
        if (highlightedIndex.value < searchResults.value.length - 1) {
          highlightedIndex.value++;
        }
      };

      const navigateUp = () => {
        if (highlightedIndex.value > 0) {
          highlightedIndex.value--;
        }
      };

      const validate = () => {
        const errorMessage = validateInput(props.modelValue, props.rules);
        localError.value = errorMessage;
        emit('validate', errorMessage);
      };

      // Close on outside click. The panel lives on document.body once Tippy
      // owns it, so a click on an option is outside the container and would
      // otherwise close the dropdown before the option handler runs.
      const onOutsideClick = (event) => {
        if (!isOpen.value) return;
        if (containerRef.value?.contains(event.target)) return;
        if (tippyInstance?.popper?.contains(event.target)) return;
        closeDropdown();
      };

      onMounted(async () => {
        document.addEventListener('mousedown', onOutsideClick);

        await nextTick();
        if (containerRef.value && dropdownRef.value) {
          tippyInstance = tippy(containerRef.value, {
            content: dropdownRef.value,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
            maxWidth: 'none',
            offset: [0, 4],
            popperOptions: {
              modifiers: [
                { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
              ],
            },
            onShow(instance) {
              const width = containerRef.value?.offsetWidth;
              if (width) instance.popper.style.width = `${width}px`;
            },
            // The search input is only focusable once Tippy has mounted the
            // panel, which is after openDropdown's own nextTick.
            onShown() {
              searchInputRef.value?.focus();
            },
          });
        }

        await loadInitialLeads();
      });

      onBeforeUnmount(() => {
        document.removeEventListener('mousedown', onOutsideClick);
        tippyInstance?.destroy();
        tippyInstance = null;
        if (searchDebounceTimer.value) {
          clearTimeout(searchDebounceTimer.value);
        }
      });

      watch(isOpen, (open) => {
        if (!tippyInstance) return;
        if (open) tippyInstance.show();
        else tippyInstance.hide();
      });

      // The result list grows and shrinks while searching, so the placement
      // has to be recalculated or the panel keeps the position it opened with.
      watch(searchResults, () => {
        if (!isOpen.value) return;
        nextTick(() => tippyInstance?.popperInstance?.update());
      });

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      return {
        localError,
        touched,
        isOpen,
        searchTerm,
        searchResults,
        isSearching,
        highlightedIndex,
        searchInputRef,
        containerRef,
        dropdownRef,
        selectedLeadData,
        getStatusLabel,
        toggleDropdown,
        closeDropdown,
        onSearch,
        selectLead,
        onCreateClick,
        selectHighlighted,
        navigateDown,
        navigateUp,
        validate,
      };
    },
  };
</script>

<style scoped lang="scss">
  .searchable-select {
    position: relative;

    &__trigger {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      // Match global input height: padding 0.5rem + line-height 1.25rem + 2px border
      padding: 0.5rem 0.75rem;
      background: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      box-shadow:
        0 0 #0000,
        0 0 #0000,
        0 1px 2px 0 rgb(0 0 0 / 0.05);
      cursor: pointer;
      user-select: none;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        border-color: var(--slate-300);
      }

      &--disabled {
        background-color: var(--slate-100);
        border-color: var(--slate-200);
        color: var(--slate-500);
        cursor: not-allowed;
        pointer-events: none;
      }
    }

    &--open &__trigger {
      border-color: var(--brand-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &__placeholder-text {
      flex: 1;
      color: var(--slate-400);
      font-size: 0.875rem;
      line-height: 1.25rem;
    }

    &__selected {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex: 1;
      min-width: 0;
    }

    &__selected-name {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--slate-800);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__chevron {
      flex-shrink: 0;
      color: var(--slate-400);
      transform: rotate(180deg);
      transition: transform 0.2s ease;
    }

    &--open &__chevron {
      transform: rotate(0deg);
    }

    // Hidden until Tippy mounts it into the popper; the panel's frame comes
    // from the shared 'dropdown' theme.
    &__dropdown {
      display: none;
      overflow: hidden;
      border-radius: 0.5rem;
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--slate-100);
      background: var(--slate-50);
    }

    &__search-icon {
      flex-shrink: 0;
      color: var(--slate-400);
    }

    &__search-input {
      flex: 1;
      border: none;
      background: transparent;
      outline: none;
      font-size: 0.875rem;
      color: var(--slate-700);

      &::placeholder {
        color: var(--slate-400);
      }
    }

    &__create {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--brand-500);
      background: white;
      border-top: 1px solid var(--slate-100);
      cursor: pointer;
      transition: background-color 0.15s ease;
      position: sticky;
      bottom: 0;

      &:hover {
        background-color: var(--brand-50);
      }
    }

    &__create-icon {
      font-size: 1rem;
      line-height: 1;
      color: var(--brand-500);
    }

    &__loading,
    &__empty {
      padding: 1rem 0.75rem;
      text-align: center;
      color: var(--slate-500);
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    &__options {
      max-height: 260px;
      overflow-y: auto;
    }

    &__option {
      padding: 0.625rem 0.75rem;
      cursor: pointer;
      border-bottom: 1px solid var(--slate-100);
      transition: background-color 0.15s ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover,
      &--highlighted {
        background-color: var(--slate-50);
      }

      &--selected {
        background-color: var(--brand-50);
      }

      &--selected:hover,
      &--selected#{&}--highlighted {
        background-color: var(--brand-100);
      }

      &:active {
        background-color: var(--slate-100);
      }
    }
  }

  .lead-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__name {
      font-weight: 500;
      color: var(--slate-900);
      font-size: 0.875rem;
    }

    &__email {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin-top: 0.125rem;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 2px 6px;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: lowercase;
      flex-shrink: 0;

      &--new {
        background-color: var(--blue-50);
        color: var(--blue-700);
        border: 1px solid var(--blue-200);
      }

      &--contacted {
        background-color: var(--amber-50);
        color: var(--amber-700);
        border: 1px solid var(--amber-200);
      }

      &--introduction {
        background-color: var(--indigo-50);
        color: var(--indigo-700);
        border: 1px solid var(--indigo-200);
      }

      &--qualified {
        background-color: var(--teal-50);
        color: var(--teal-700);
        border: 1px solid var(--teal-200);
      }

      &--converted {
        background-color: var(--green-50);
        color: var(--green-700);
        border: 1px solid var(--green-200);
      }

      &--lost {
        background-color: var(--red-50);
        color: var(--red-700);
        border: 1px solid var(--red-200);
      }
    }
  }

  .status-dot {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    flex-shrink: 0;

    &--new {
      background-color: var(--blue-500);
    }

    &--contacted {
      background-color: var(--amber-500);
    }

    &--introduction {
      background-color: var(--indigo-500);
    }

    &--qualified {
      background-color: var(--teal-500);
    }

    &--converted {
      background-color: var(--green-500);
    }

    &--lost {
      background-color: var(--red-500);
    }
  }
</style>

<style lang="scss">
  .tippy-box[data-theme~='dropdown'] .searchable-select__dropdown {
    display: block;
  }
</style>
